import {
  createContext,
  useContext,
  useRef,
  useCallback,
  useEffect,
} from 'react';

interface FormSubmitFn {
  (): Promise<void>;
}

interface PropertyEditContextValue {
  registerForm: (submitFn: FormSubmitFn) => void;
  unregisterForm: () => void;
  submitForm: () => Promise<void>;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

const PropertyEditContext = createContext<PropertyEditContextValue | null>(
  null,
);

export function usePropertyEditContext() {
  const context = useContext(PropertyEditContext);
  if (!context) {
    throw new Error(
      'usePropertyEditContext must be used within PropertyEditProvider',
    );
  }
  return context;
}

export function useFormRegistration(submitFn: FormSubmitFn) {
  const context = useContext(PropertyEditContext);
  const submitFnRef = useRef(submitFn);

  // Keep ref up to date
  submitFnRef.current = submitFn;

  useEffect(() => {
    if (context) {
      // Register on mount
      const wrappedSubmitFn = () => submitFnRef.current();
      context.registerForm(wrappedSubmitFn);
      // Unregister on unmount
      return () => context.unregisterForm();
    }
  }, []); // Empty deps - only run on mount/unmount
}

export { PropertyEditContext };
